import { useSession, useUser } from "@clerk/clerk-react";
import { useCallback, useState } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "components/ui/table";
import { Badge } from "components/ui/badge";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "components/ui/tooltip";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import {
  File, FolderOpen, ArrowSquareOut, Warning, Robot, Tag, X,
  CheckCircle, Brain, Rocket, Cube, Scissors, Database, Calculator,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import BulkAnalysisProgress from "shared/components/BulkAnalysisProgress";
import ProposeLabelModal from "shared/components/searchBar/components/ProposeLabelModal";
import { PdfSplitModal, SplitResult } from "./PdfSplitModal";
import { PageInfoBanner } from "@/shared/components/walkthrough";

import type {
  DropzoneTabProps,
  DropzoneFile,
  ClassificationResult,
  NewLabelSuggestion,
  DatabaseFileLabel,
  SuggestedQuestion,
} from "./types";
import {
  formatFileSize, getFileIcon, getFileTypeLabel, getConfidenceColor, isPdfFile,
} from "./utils";
import {
  useDropzoneData,
  useFileClassification,
  useFileSaving,
  useDueDiligenceAnswers,
  useFileAssignment,
} from "./hooks";
import {
  StandardFieldsPanel,
  NewLabelSuggestions,
  DDQuestionMatches,
  ProposedDDQuestions,
  DdAnswersSection,
} from "./components";

export const DropzoneTab = ({ buildingId }: DropzoneTabProps) => {
  const { session } = useSession();
  const { user } = useUser();

  // --- Hooks ---
  const classification = useFileClassification(
    buildingId, session, user,
    () => { dataHook.refetchDropzoneFiles(); },
  );

  const dataHook = useDropzoneData(
    buildingId, session,
    useCallback((results: Record<string, ClassificationResult>) => {
      classification.mergeCachedResults(results);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const ddAnswers = useDueDiligenceAnswers(session, classification.classificationResults);

  const assignment = useFileAssignment(
    classification.classificationResults,
    dataHook.buildingUnits,
    dataHook.buildingAttributes
  );

  const saving = useFileSaving(buildingId, session, user, {
    classificationResults: classification.classificationResults,
    fileLabels: dataHook.fileLabels,
    getEffectiveUnitId: assignment.getEffectiveUnitId,
    getEffectiveBuildingAttributeId: assignment.getEffectiveBuildingAttributeId,
    prepareDdAnswersForSave: ddAnswers.prepareDdAnswersForSave,
  });

  // --- Modal state for propose label / accept proposal ---
  const [proposeLabelModalOpen, setProposeLabelModalOpen] = useState(false);
  const [proposedLabelSuggestion, setProposedLabelSuggestion] = useState<NewLabelSuggestion | null>(null);
  const [fileForNewLabel, setFileForNewLabel] = useState<DropzoneFile | null>(null);

  const [acceptProposalModalOpen, setAcceptProposalModalOpen] = useState(false);
  const [proposalLabelName, setProposalLabelName] = useState('');
  const [proposalResourceType, setProposalResourceType] = useState<'building' | 'unit'>('building');
  const [proposalPreSuggestedQuestions, setProposalPreSuggestedQuestions] = useState<SuggestedQuestion[]>([]);
  const [selectedProposalFileId, setSelectedProposalFileId] = useState<string | null>(null);

  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [selectedFileForSplit, setSelectedFileForSplit] = useState<{ file: DropzoneFile; folderId: string } | null>(null);

  // Reset saved file IDs when buildingId changes (handled in useDropzoneData for other state)
  // We need this because savedFileIds and classificationResults reset together
  // Classification results reset is in useFileClassification via buildingId change

  // --- Handlers ---
  const handleProposeLabelOpen = (suggestion: NewLabelSuggestion, file: DropzoneFile) => {
    setProposedLabelSuggestion(suggestion);
    setFileForNewLabel(file);
    setProposeLabelModalOpen(true);
  };

  const handleAcceptProposal = (
    labelName: string,
    resourceType: 'building' | 'unit',
    suggestedQuestions: SuggestedQuestion[],
    fileId: string
  ) => {
    setProposalLabelName(labelName);
    setProposalResourceType(resourceType);
    setProposalPreSuggestedQuestions(suggestedQuestions);
    setSelectedProposalFileId(fileId);
    setAcceptProposalModalOpen(true);
  };

  const handleClearClassification = async (fileId: string) => {
    await classification.clearClassification(fileId);
    ddAnswers.clearDdStateForFile(fileId);
  };

  // --- Loading / Error / Empty ---
  if (dataHook.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-sm text-neutral-secondary-mid">
          Dropzone bestanden laden...
        </span>
      </div>
    );
  }

  if (dataHook.error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Warning className="h-12 w-12 text-amber-500 mb-4" weight="duotone" />
        <p className="text-sm font-medium text-neutral-primary">{dataHook.error}</p>
        <p className="text-xs text-neutral-secondary-mid mt-1">Probeer de pagina te vernieuwen</p>
      </div>
    );
  }

  if (!dataHook.dropzoneData || dataHook.dropzoneData.files.length === 0) {
    const folders = dataHook.dropzoneData?.dropzoneFolders ?? [];
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FolderOpen className="h-12 w-12 text-neutral-secondary-mid mb-4" weight="duotone" />
        <p className="text-sm font-medium text-neutral-primary">Geen bestanden gevonden</p>
        <p className="text-xs text-neutral-secondary-mid mt-1 max-w-md">
          {folders.length > 0
            ? `Er ${folders.length === 1 ? "is" : "zijn"} ${folders.length} _Dropzone map${folders.length !== 1 ? "pen" : ""} gevonden, maar ${folders.length === 1 ? "deze is" : "deze zijn"} leeg. Submappen worden ook gescand (max. 10 niveaus diep).`
            : "Er zijn geen _Dropzone mappen gevonden voor dit gebouw. Maak een map met de naam '_Dropzone' in de hoofdmap van het gebouw of in een unit map. Submappen worden ook gescand (max. 10 niveaus diep)."}
        </p>
        {folders.length > 0 && (
          <div className="mt-4 text-xs text-neutral-secondary-mid">
            <p className="font-medium mb-1">Gevonden mappen:</p>
            <ul className="list-disc list-inside">
              {folders.map((folder) => (
                <li key={folder.folderId}>{folder.path}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  const { dropzoneData } = dataHook;

  // Group files by path
  const filesByPath = dropzoneData.files.reduce(
    (acc, file) => {
      if (!acc[file.path]) acc[file.path] = [];
      acc[file.path].push(file);
      return acc;
    },
    {} as Record<string, DropzoneFile[]>
  );

  return (
    <div className="flex flex-col gap-6">
      <PageInfoBanner pageId="dropzone" />

      {/* Summary Bar */}
      <div className="flex items-center justify-between" data-tour="dropzone-summary">
        <div className="flex items-center gap-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="gap-1.5 cursor-help">
                  <FolderOpen className="h-4 w-4" weight="duotone" />
                  {dropzoneData.dropzoneFolders.length} Dropzone map
                  {dropzoneData.dropzoneFolders.length !== 1 ? "pen" : ""}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Submappen binnen _Dropzone worden ook gescand</p>
                <p className="text-xs text-neutral-400">(maximaal 10 niveaus diep)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Badge variant="outline" className="gap-1.5">
            <File className="h-4 w-4" weight="duotone" />
            {dropzoneData.files.length} bestand{dropzoneData.files.length !== 1 ? "en" : ""}
          </Badge>
          {saving.savedFileIds.size > 0 && (
            <Badge variant="outline" className="gap-1.5 text-green-600">
              <CheckCircle className="h-4 w-4" weight="duotone" />
              {saving.savedFileIds.size} opgeslagen
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2" data-tour="dropzone-actions">
          {/* AI Provider Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1.5">
                  <span className="text-xs text-neutral-500 mr-1">AI:</span>
                  <button
                    onClick={() => classification.setAiProvider('claude')}
                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                      classification.aiProvider === 'claude'
                        ? 'bg-violet-600 text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    Claude
                  </button>
                  <button
                    onClick={() => classification.setAiProvider('google_gpt')}
                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                      classification.aiProvider === 'google_gpt'
                        ? 'bg-violet-600 text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    Google+GPT
                  </button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="font-medium">AI Provider voor PDF analyse</p>
                <p className="text-xs text-neutral-400 mt-1">
                  <strong>Claude:</strong> Native PDF vision (sneller, goedkoper voor Claude Files API)<br />
                  <strong>Google+GPT:</strong> Google Vision OCR + GPT-4o (alternatief)
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Button
            onClick={classification.handleStartBulkAnalysis}
            variant="outline"
            className="gap-1.5 border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 hover:border-violet-300"
          >
            <Brain className="h-4 w-4" weight="duotone" />
            Bulk AI Analyse
          </Button>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={classification.handleStartBackgroundDDProcessing}
                  variant="outline"
                  disabled={classification.isStartingBackgroundProcessing}
                  className="gap-1.5 border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 disabled:opacity-50"
                >
                  {classification.isStartingBackgroundProcessing ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <Rocket className="h-4 w-4" weight="duotone" />
                  )}
                  DD Achtergrond
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="font-medium">DD verwerking op achtergrond</p>
                <p className="text-xs text-neutral-400 mt-1">
                  Analyseert alle dropzone-bestanden voor due diligence vragen.
                  Eerder geanalyseerde bestanden worden overgeslagen.
                  Je ontvangt Slack-updates bij 25%, 50%, 75% en 100%.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Bulk Analysis Progress */}
      {classification.showBulkProgress && classification.currentBulkJob && (
        <BulkAnalysisProgress
          job={classification.currentBulkJob}
          onCancel={classification.handleBulkAnalysisCancel}
          onClose={classification.handleBulkAnalysisClose}
          onRefreshStatus={classification.handleBulkAnalysisRefresh}
          isPolling={classification.isBulkAnalysisPolling}
        />
      )}

      {/* Files grouped by location */}
      <div data-tour="dropzone-results">
        {Object.entries(filesByPath).map(([path, files]) => (
          <div key={path} className="space-y-3">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-amber-500" weight="duotone" />
              <h3 className="text-sm font-medium text-neutral-primary">{path}</h3>
              <Badge variant="secondary" className="text-xs">
                {files.length} bestand{files.length !== 1 ? "en" : ""}
              </Badge>
            </div>

            <div className="rounded-lg border border-neutral-primary/10">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="w-[35%]">Bestandsnaam</TableHead>
                    <TableHead className="w-[10%]">Type</TableHead>
                    <TableHead className="w-[10%]">Grootte</TableHead>
                    <TableHead className="w-[30%]">AI Classificatie</TableHead>
                    <TableHead className="w-[15%] text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file) => {
                    const classResult = classification.classificationResults[file.id];
                    const isClassifying = classification.classifyingFileIds.has(file.id);
                    const fileProgress = classification.classificationProgress[file.id] || 0;
                    const isSaved = saving.savedFileIds.has(file.id);

                    return (
                      <TableRow
                        key={file.id}
                        className={`hover:bg-neutral-50/50 ${isSaved ? "bg-green-50/30" : ""}`}
                      >
                        {/* File name */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getFileIcon(file.type)}
                            <span className="text-sm font-medium truncate max-w-[250px]">{file.name}</span>
                            {isSaved && <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" weight="fill" />}
                          </div>
                        </TableCell>

                        {/* File type */}
                        <TableCell>
                          <Badge variant="outline" className="text-xs font-normal">
                            {getFileTypeLabel(file.type)}
                          </Badge>
                        </TableCell>

                        {/* File size */}
                        <TableCell className="text-sm text-neutral-secondary-mid">
                          {formatFileSize(file.size)}
                        </TableCell>

                        {/* AI Classification */}
                        <TableCell>
                          {classResult ? (
                            <div className="space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 space-y-1.5">
                                  {classResult.suggestedLabels.length > 0 ? (
                                    <>
                                      {/* Label buttons */}
                                      <div className="flex flex-wrap gap-1.5">
                                        {classResult.suggestedLabels.map((suggestion, idx) => {
                                          const labelInfo = dataHook.fileLabels.find(
                                            (l) => l.label.toLowerCase() === suggestion.label.toLowerCase()
                                          );
                                          const isUnitLabel = labelInfo?.resourceType === 'unit';

                                          return (
                                            <button
                                              key={idx}
                                              onClick={() => !isSaved && saving.handleDirectLabelSave(file, suggestion.label)}
                                              disabled={isSaved || saving.savingFile}
                                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium transition-colors ${
                                                isSaved
                                                  ? "bg-neutral-50 text-neutral-400 cursor-not-allowed"
                                                  : "bg-white hover:bg-moss-50 hover:border-moss-primary/50 border-neutral-200 cursor-pointer"
                                              }`}
                                              title={`Klik om "${suggestion.label}" te selecteren (${isUnitLabel ? 'Unit' : 'Gebouw'})`}
                                            >
                                              <Tag className="h-3 w-3 text-moss-primary flex-shrink-0" weight="duotone" />
                                              <span className="truncate max-w-[120px]">{suggestion.label}</span>
                                              <span className={`px-1 py-0.5 rounded text-[9px] ${isUnitLabel ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100 text-neutral-600'}`}>
                                                {isUnitLabel ? 'U' : 'G'}
                                              </span>
                                              <span className={`px-1 py-0.5 rounded text-[10px] ${getConfidenceColor(suggestion.confidence)}`}>
                                                {Math.round(suggestion.confidence * 100)}%
                                              </span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {!isSaved && (
                                        <button
                                          onClick={() => {
                                            saving.setSelectedFileForLabeling(file);
                                            saving.setLabelPickerOpen(true);
                                          }}
                                          className="text-xs text-neutral-secondary-mid hover:text-moss-primary transition-colors underline"
                                        >
                                          Ander label kiezen...
                                        </button>
                                      )}
                                      {/* New label suggestions */}
                                      {!isSaved && classResult.newLabelSuggestions && classResult.newLabelSuggestions.length > 0 && (
                                        <NewLabelSuggestions
                                          suggestions={classResult.newLabelSuggestions}
                                          file={file}
                                          onPropose={handleProposeLabelOpen}
                                        />
                                      )}
                                      {/* DD Question Matches */}
                                      {!isSaved && classResult.ddQuestionMatches && classResult.ddQuestionMatches.length > 0 && (
                                        <DDQuestionMatches
                                          matches={classResult.ddQuestionMatches}
                                          selectedLabel={classResult.suggestedLabels?.[0]}
                                          linkingLabelToQuestion={ddAnswers.linkingLabelToQuestion}
                                          onLinkLabel={ddAnswers.handleLinkLabelToQuestion}
                                        />
                                      )}
                                      {/* Proposed DD Questions */}
                                      {!isSaved && classResult.proposedDDQuestions && classResult.proposedDDQuestions.length > 0 && (
                                        <ProposedDDQuestions
                                          proposals={classResult.proposedDDQuestions}
                                          fileId={file.id}
                                          onAcceptProposal={handleAcceptProposal}
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-neutral-secondary-mid italic">Geen passend label gevonden</span>
                                      {!isSaved && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            saving.setSelectedFileForLabeling(file);
                                            saving.setLabelPickerOpen(true);
                                          }}
                                          className="gap-1 text-xs h-7"
                                        >
                                          <Tag className="h-3 w-3" weight="duotone" />
                                          Kies label
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {!isSaved && (
                                    <button
                                      onClick={() => classification.handleReanalyzeFile(file, assignment.getEffectiveUnitId(file))}
                                      disabled={classification.reanalyzingFileId === file.id}
                                      className="p-1 hover:bg-blue-100 rounded transition-colors"
                                      title="Opnieuw analyseren"
                                    >
                                      {classification.reanalyzingFileId === file.id ? (
                                        <Spinner className="h-4 w-4" />
                                      ) : (
                                        <ArrowClockwise className="h-4 w-4 text-blue-600" weight="bold" />
                                      )}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleClearClassification(file.id)}
                                    className="p-1 hover:bg-neutral-100 rounded transition-colors"
                                    title="Verwijder classificatie"
                                  >
                                    <X className="h-4 w-4 text-neutral-secondary-mid" />
                                  </button>
                                </div>
                              </div>

                              {/* Cache / Cost info */}
                              <div className="flex items-center gap-3 text-[10px] text-neutral-500">
                                {classResult.fromCache && (
                                  <div className="flex items-center gap-1.5">
                                    <Database className="h-3 w-3" weight="duotone" />
                                    <span>Uit cache ({new Date(classResult.analyzedAt).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})</span>
                                  </div>
                                )}
                                {classResult.analysisCost && !classResult.fromCache && (
                                  <div className="flex items-center gap-1.5 text-moss-primary">
                                    <Calculator className="h-3 w-3" weight="duotone" />
                                    <span>€{classResult.analysisCost.totalCostEur.toFixed(4)} ({(classResult.analysisCost.totalInputTokens + classResult.analysisCost.totalOutputTokens).toLocaleString('nl-BE')} tokens)</span>
                                  </div>
                                )}
                              </div>

                              {/* Unit picker */}
                              {assignment.isUnitLevelLabel(file) && (
                                <div className="flex items-center gap-2 text-xs">
                                  <Cube className="h-3.5 w-3.5 text-purple-600" weight="duotone" />
                                  <span className="text-neutral-600">Unit:</span>
                                  {!isSaved ? (
                                    <button
                                      onClick={() => assignment.openUnitPicker(file)}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs transition-colors ${
                                        assignment.getEffectiveUnitId(file)
                                          ? 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                                          : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                                      }`}
                                    >
                                      {assignment.getEffectiveUnitId(file) ? (
                                        <>
                                          <span>{assignment.getUnitDisplayName(assignment.getEffectiveUnitId(file))}</span>
                                          {classResult.detectedUnitId === assignment.getEffectiveUnitId(file) && !assignment.unitOverrides[file.id] && (
                                            <span className="text-[9px] text-purple-500">(AI)</span>
                                          )}
                                          {assignment.unitOverrides[file.id] !== undefined && (
                                            <span className="text-[9px] text-blue-500">(aangepast)</span>
                                          )}
                                        </>
                                      ) : (
                                        <span className="italic">Selecteer unit...</span>
                                      )}
                                    </button>
                                  ) : (
                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                      assignment.getEffectiveUnitId(file) ? 'bg-purple-50 text-purple-700' : 'bg-neutral-100 text-neutral-500 italic'
                                    }`}>
                                      {assignment.getEffectiveUnitId(file) ? assignment.getUnitDisplayName(assignment.getEffectiveUnitId(file)) : 'Geen unit'}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Attribute picker */}
                              {assignment.isAttributeLevelLabel(file) && (
                                <div className="flex items-center gap-2 text-xs">
                                  <Database className="h-3.5 w-3.5 text-teal-600" weight="duotone" />
                                  <span className="text-neutral-600">Attribuut:</span>
                                  {!isSaved ? (
                                    <button
                                      onClick={() => assignment.openAttributePicker(file)}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs transition-colors ${
                                        assignment.getEffectiveBuildingAttributeId(file)
                                          ? 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100'
                                          : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                                      }`}
                                    >
                                      {assignment.getEffectiveBuildingAttributeId(file) ? (
                                        <>
                                          <span>{assignment.getAttributeDisplayName(assignment.getEffectiveBuildingAttributeId(file))}</span>
                                          {classResult.detectedAttributeId === assignment.getEffectiveBuildingAttributeId(file) && !assignment.attributeOverrides[file.id] && (
                                            <span className="text-[9px] text-purple-500">(AI)</span>
                                          )}
                                          {assignment.attributeOverrides[file.id] !== undefined && (
                                            <span className="text-[9px] text-blue-500">(aangepast)</span>
                                          )}
                                        </>
                                      ) : (
                                        <span className="italic">Selecteer attribuut...</span>
                                      )}
                                    </button>
                                  ) : (
                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                      assignment.getEffectiveBuildingAttributeId(file) ? 'bg-teal-50 text-teal-700' : 'bg-neutral-100 text-neutral-500 italic'
                                    }`}>
                                      {assignment.getEffectiveBuildingAttributeId(file) ? assignment.getAttributeDisplayName(assignment.getEffectiveBuildingAttributeId(file)) : 'Geen attribuut'}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Document summary */}
                              {classResult.documentSummary && (
                                <p className="text-xs text-neutral-secondary-mid line-clamp-2">
                                  {classResult.documentSummary}
                                </p>
                              )}

                              {/* Standard fields */}
                              {classResult.extractedStandardFields && (
                                <StandardFieldsPanel
                                  fileId={file.id}
                                  fields={classResult.extractedStandardFields}
                                  isExpanded={ddAnswers.expandedStandardFields.has(file.id)}
                                  expandedAIDetails={ddAnswers.expandedAIDetails}
                                  onToggleExpand={ddAnswers.toggleStandardFieldsExpanded}
                                  onToggleAIDetails={ddAnswers.toggleAIDetailsExpanded}
                                />
                              )}

                              {/* DD Answers */}
                              {classResult.dueDiligenceAnswers && classResult.dueDiligenceAnswers.extractedAnswers.length > 0 && (
                                <DdAnswersSection
                                  fileId={file.id}
                                  classification={classResult}
                                  isSaved={isSaved}
                                  isExpanded={ddAnswers.expandedDdAnswers.has(file.id)}
                                  expandedConditionDetails={ddAnswers.expandedConditionDetails}
                                  expandedExistingAnswers={ddAnswers.expandedExistingAnswers}
                                  expandedAIDetails={ddAnswers.expandedAIDetails}
                                  onToggleDdExpanded={ddAnswers.toggleDdAnswersExpanded}
                                  onToggleConditionDetails={ddAnswers.toggleConditionDetailsExpanded}
                                  onToggleExistingAnswer={ddAnswers.toggleExistingAnswerExpanded}
                                  onToggleAIDetails={ddAnswers.toggleAIDetailsExpanded}
                                  onToggleSkipped={ddAnswers.toggleSkippedDdAnswer}
                                  onUpdateAnswer={ddAnswers.updateEditedDdAnswer}
                                  isDdAnswerSkipped={ddAnswers.isDdAnswerSkipped}
                                  getEffectiveDdAnswer={ddAnswers.getEffectiveDdAnswer}
                                  getEffectiveConditionPassed={ddAnswers.getEffectiveConditionPassed}
                                  getConditionOverride={ddAnswers.getConditionOverride}
                                  onToggleConditionOverride={ddAnswers.toggleConditionOverride}
                                  onUpdateOverrideReason={ddAnswers.updateConditionOverrideReason}
                                />
                              )}
                            </div>
                          ) : isSaved ? (
                            <span className="text-xs text-green-600 font-medium">Opgeslagen</span>
                          ) : isClassifying ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1.5 text-xs text-violet-700">
                                <Spinner className="h-3.5 w-3.5" />
                                <span>Analyseren...</span>
                              </div>
                              <div className="w-20 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                                <div className="h-full bg-violet-500 transition-all duration-300 ease-out" style={{ width: `${fileProgress}%` }} />
                              </div>
                              <span className="text-[10px] text-neutral-400 min-w-[28px]">{Math.round(fileProgress)}%</span>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => classification.handleClassifyFile(file, assignment.getEffectiveUnitId(file))}
                              className="gap-1.5 text-xs"
                              data-tour="dropzone-classify-button"
                            >
                              <Robot className="h-3.5 w-3.5" weight="duotone" />
                              Classificeer met AI
                            </Button>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isPdfFile(file.type, file.name) && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const matchingFolders = dropzoneData?.dropzoneFolders.filter(
                                          (f) => path.startsWith(f.path)
                                        ) || [];
                                        const folder = matchingFolders.sort((a, b) => b.path.length - a.path.length)[0];
                                        if (folder) {
                                          setSelectedFileForSplit({ file, folderId: folder.folderId });
                                          setSplitModalOpen(true);
                                        } else {
                                          toast.error("Kan de map niet vinden voor dit bestand");
                                        }
                                      }}
                                      className="h-8 w-8 p-0 text-neutral-500 hover:text-purple-600 hover:bg-purple-50"
                                    >
                                      <Scissors className="h-4 w-4" weight="duotone" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>PDF splitsen</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <a
                              href={file.sharePointUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm font-medium text-moss-primary hover:text-moss-primary/80 transition-colors"
                            >
                              Openen
                              <ArrowSquareOut className="h-4 w-4" weight="bold" />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </div>

      {/* === MODALS === */}

      {/* Label Picker Modal */}
      <UniversalPickerModal
        isOpen={saving.labelPickerOpen}
        mode="file-label"
        title={
          <div className="flex flex-col gap-1">
            <span>Selecteer label voor bestand</span>
            {saving.selectedFileForLabeling && (
              <span className="text-xs text-neutral-secondary-mid font-normal">{saving.selectedFileForLabeling.name}</span>
            )}
          </div>
        }
        onClose={() => { saving.setLabelPickerOpen(false); saving.setSelectedFileForLabeling(null); }}
        onConfirm={saving.handleLabelSelect}
        config={{
          databaseFileLabels: saving.getEnhancedFileLabels(),
          currentBuildingId: buildingId,
          allowRemoval: false,
          searchPlaceholder: "Zoek label...",
        }}
      />

      {/* AI Suggestions floating panel */}
      {saving.labelPickerOpen && saving.selectedFileForLabeling &&
        classification.classificationResults[saving.selectedFileForLabeling.id]?.suggestedLabels?.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 max-w-xs bg-white rounded-lg shadow-lg border border-neutral-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Robot className="h-5 w-5 text-moss-primary" weight="duotone" />
            <span className="text-sm font-medium">AI Suggesties</span>
          </div>
          <div className="space-y-2">
            {classification.classificationResults[saving.selectedFileForLabeling.id]?.suggestedLabels.map((suggestion, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium truncate">{suggestion.label}</span>
                <span className={`px-1.5 py-0.5 rounded flex-shrink-0 ${getConfidenceColor(suggestion.confidence)}`}>
                  {Math.round(suggestion.confidence * 100)}%
                </span>
              </div>
            ))}
          </div>
          {classification.classificationResults[saving.selectedFileForLabeling.id]?.documentSummary && (
            <p className="mt-2 text-xs text-neutral-secondary-mid border-t pt-2">
              {classification.classificationResults[saving.selectedFileForLabeling.id].documentSummary}
            </p>
          )}
        </div>
      )}

      {/* Unit Picker Modal */}
      <UniversalPickerModal
        isOpen={assignment.unitPickerOpen}
        mode="unit"
        title={
          <div className="flex flex-col gap-1">
            <span>Selecteer unit voor {assignment.selectedFileForUnitPicker?.name}</span>
            {classification.classificationResults[assignment.selectedFileForUnitPicker?.id || ""]?.detectedUnitId && (
              <span className="text-xs text-neutral-secondary-mid font-normal">
                AI suggestie: {classification.classificationResults[assignment.selectedFileForUnitPicker?.id || ""]?.detectedUnitId}
              </span>
            )}
          </div>
        }
        onClose={() => { assignment.setUnitPickerOpen(false); }}
        onConfirm={assignment.handleUnitPickerConfirm}
        config={{ currentBuildingId: buildingId, allowRemoval: true, searchPlaceholder: "Zoek unit..." }}
      />

      {/* Attribute Picker Modal */}
      {assignment.attributePickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Database className="h-5 w-5 text-teal-600" weight="duotone" />
                Selecteer attribuut
              </h3>
              <p className="text-sm text-neutral-secondary-mid mt-1">
                Voor: {assignment.selectedFileForAttributePicker?.name}
              </p>
              {assignment.selectedFileForAttributePicker && classification.classificationResults[assignment.selectedFileForAttributePicker.id]?.detectedAttributeId && (
                <p className="text-xs text-purple-600 mt-1">
                  AI suggestie: {assignment.getAttributeDisplayName(classification.classificationResults[assignment.selectedFileForAttributePicker.id]?.detectedAttributeId ?? null)}
                </p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {dataHook.buildingAttributes.length === 0 ? (
                <div className="text-center py-8 text-neutral-secondary-mid">
                  <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Geen attributen gevonden voor dit gebouw</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <button
                    onClick={() => assignment.handleAttributePickerConfirm(null)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-neutral-100 transition-colors text-sm flex items-center gap-2"
                  >
                    <X className="h-4 w-4 text-neutral-400" />
                    <span className="text-neutral-500 italic">Geen attribuut</span>
                  </button>
                  {dataHook.buildingAttributes.map((attr) => (
                    <button
                      key={attr.id}
                      onClick={() => assignment.handleAttributePickerConfirm(attr)}
                      className={`w-full text-left px-3 py-2 rounded-md hover:bg-teal-50 transition-colors text-sm ${
                        assignment.getEffectiveBuildingAttributeId(assignment.selectedFileForAttributePicker!) === attr.id
                          ? 'bg-teal-100 border border-teal-300'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-neutral-800">{attr.name}</span>
                          {attr.template?.category && (
                            <span className="ml-2 text-xs text-neutral-500">({attr.template.category})</span>
                          )}
                        </div>
                        {assignment.getEffectiveBuildingAttributeId(assignment.selectedFileForAttributePicker!) === attr.id && (
                          <CheckCircle className="h-4 w-4 text-teal-600" weight="fill" />
                        )}
                      </div>
                      {attr.description && (
                        <p className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{attr.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 border-t flex justify-end">
              <Button variant="outline" size="sm" onClick={() => assignment.setAttributePickerOpen(false)}>
                Annuleren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Saving overlay */}
      {saving.savingFile && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <Spinner className="h-6 w-6" />
            <span className="text-sm font-medium">Bestand opslaan...</span>
          </div>
        </div>
      )}

      {/* Propose New Label Modal */}
      {proposedLabelSuggestion && (
        <ProposeLabelModal
          isOpen={proposeLabelModalOpen}
          onClose={() => {
            setProposeLabelModalOpen(false);
            setProposedLabelSuggestion(null);
            setFileForNewLabel(null);
          }}
          proposedLabelName={proposedLabelSuggestion.label}
          suggestedResourceType={proposedLabelSuggestion.resourceType}
          suggestedDocumentType={proposedLabelSuggestion.documentType}
          onLabelCreated={(newLabel) => {
            const createdLabelName = newLabel.label.toLowerCase().trim();
            classification.setClassificationResults((prev) => {
              const updated = { ...prev };
              for (const fileId of Object.keys(updated)) {
                const existing = updated[fileId];
                if (!existing) continue;
                const isCurrentFile = fileForNewLabel && fileId === fileForNewLabel.id;
                const hasMatchingSuggestion = existing.newLabelSuggestions?.some(
                  (s) => s.label.toLowerCase().trim() === createdLabelName
                );
                if (isCurrentFile || hasMatchingSuggestion) {
                  const newSuggestedLabel = {
                    label: newLabel.label,
                    labelId: newLabel.id,
                    confidence: isCurrentFile ? 1.0 : 0.95,
                    reason: isCurrentFile ? 'Nieuw aangemaakt label' : 'Eerder voorgesteld, nu aangemaakt',
                  };
                  const filteredNewSuggestions = existing.newLabelSuggestions?.filter(
                    (s) => s.label.toLowerCase().trim() !== createdLabelName
                  );
                  updated[fileId] = {
                    ...existing,
                    suggestedLabels: [newSuggestedLabel, ...existing.suggestedLabels.filter((s) => s.labelId !== newLabel.id)],
                    newLabelSuggestions: filteredNewSuggestions?.length ? filteredNewSuggestions : undefined,
                  };
                }
              }
              return updated;
            });
            dataHook.setFileLabels((prev) => [
              ...prev,
              { id: newLabel.id, label: newLabel.label, category: newLabel.category, resourceType: newLabel.resourceType, documentType: newLabel.documentType, aiSuggested: false, folders: [] } as DatabaseFileLabel,
            ]);

            const otherFilesUpdated = Object.keys(classification.classificationResults).filter((fileId) => {
              if (fileForNewLabel && fileId === fileForNewLabel.id) return false;
              return classification.classificationResults[fileId]?.newLabelSuggestions?.some(
                (s) => s.label.toLowerCase().trim() === createdLabelName
              );
            }).length;

            toast.success(
              otherFilesUpdated > 0
                ? `Label "${newLabel.label}" aangemaakt en beschikbaar voor ${otherFilesUpdated + 1} bestanden`
                : `Label "${newLabel.label}" aangemaakt en geselecteerd`
            );
            setProposeLabelModalOpen(false);
            setProposedLabelSuggestion(null);
            setFileForNewLabel(null);
          }}
        />
      )}

      {/* PDF Split Modal */}
      {selectedFileForSplit && (
        <PdfSplitModal
          isOpen={splitModalOpen}
          onClose={() => { setSplitModalOpen(false); setSelectedFileForSplit(null); }}
          onSplitComplete={(result: SplitResult) => {
            dataHook.setDropzoneData((prev) => {
              if (!prev) return prev;
              const originalPath = result.folderPath;
              const folderPath = originalPath.substring(0, originalPath.lastIndexOf('/'));
              const newFiles: DropzoneFile[] = result.splits.map((split) => ({
                id: split.fileId,
                name: split.fileName,
                type: 'application/pdf',
                size: 0,
                path: folderPath ? `${folderPath}/${split.fileName}` : split.fileName,
                sharePointUrl: split.webUrl,
                unitId: null,
                buildingAttributeId: null,
              }));
              let updatedFiles = result.originalDeleted
                ? prev.files.filter((f) => f.id !== result.originalFileId)
                : prev.files;
              updatedFiles = [...updatedFiles, ...newFiles];
              return { ...prev, files: updatedFiles };
            });
          }}
          buildingId={buildingId}
          file={{ id: selectedFileForSplit.file.id, name: selectedFileForSplit.file.name, path: selectedFileForSplit.file.path }}
          folderId={selectedFileForSplit.folderId}
        />
      )}

      {/* Accept Proposed DD Question Modal */}
      {acceptProposalModalOpen && (
        <ProposeLabelModal
          isOpen={acceptProposalModalOpen}
          onClose={() => {
            setAcceptProposalModalOpen(false);
            setProposalLabelName('');
            setProposalPreSuggestedQuestions([]);
            setSelectedProposalFileId(null);
          }}
          proposedLabelName={proposalLabelName}
          suggestedResourceType={proposalResourceType}
          suggestedDocumentType="document"
          preSuggestedQuestions={proposalPreSuggestedQuestions}
          onLabelCreated={(label) => {
            if (selectedProposalFileId) {
              classification.setClassificationResults((prev) => {
                const fileResult = prev[selectedProposalFileId];
                if (!fileResult?.proposedDDQuestions) return prev;
                return {
                  ...prev,
                  [selectedProposalFileId]: {
                    ...fileResult,
                    proposedDDQuestions: fileResult.proposedDDQuestions.filter(
                      (p) => p.linkedLabelSuggestion !== proposalLabelName && p.questionText !== proposalLabelName
                    ),
                  },
                };
              });
            }
            dataHook.setFileLabels((prev) => {
              const exists = prev.some((l) => l.id === label.id);
              if (exists) return prev;
              return [...prev, { id: label.id, label: label.label, resourceType: label.resourceType, documentType: label.documentType, folders: [] }];
            });
            toast.success(`Label "${label.label}" aangemaakt met DD vragen`);
          }}
        />
      )}
    </div>
  );
};

export default DropzoneTab;
